import { Box, Icon, InlineLink, Spinner, Text } from '@fullstory/components';
import { ObjectDetails } from '@fullstory/proto/fs/services/dx-objects';
import { allSpecObjectsBySlug, fullstorySelectors } from '@fullstory/redux';
import {
  generateRoutePath,
  SETTINGS_ELEMENTS_DETAIL,
  SETTINGS_EVENTS_DETAIL,
  SETTINGS_PAGES_DETAIL,
} from '@fullstory/routes';
import { createPath } from 'history';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { getObjectIconName, toObjectDetailsWithRecords } from '../../utils';
import { getReferencesTab } from '../DQMTable';

interface ObjectReferencesTooltipContentProps {
  objectDetails: ObjectDetails[] | undefined;
  objectType: string;
  objectId: string;
  loading: boolean;
  supportsProperties: boolean;
}

type EmptyStateProps = {
  objectType: string;
};

const EmptyState: React.FC<EmptyStateProps> = ({ objectType }) => (
  <Box data-component="EmptyState" padding={[0, 8, 8, 0]}>
    <Text appearance="bodyMedium">
      <FormattedMessage
        defaultMessage="No library objects are using this {objectType}."
        id="JBW03X"
        values={{
          objectType: objectType.toLowerCase(),
        }}
      />
    </Text>
  </Box>
);

const getObjectTypePath = (objectType: string) => {
  switch (objectType) {
    case 'event':
      return SETTINGS_EVENTS_DETAIL;
    case 'page':
      return SETTINGS_PAGES_DETAIL;
    case 'element':
      return SETTINGS_ELEMENTS_DETAIL;
    default:
      throw new Error(`Unsupported object type: ${objectType}`);
  }
};

export const ObjectReferencesTooltipContent: React.FC<ObjectReferencesTooltipContentProps> = ({
  objectDetails,
  objectType,
  objectId,
  loading,
  supportsProperties,
}) => {
  const history = useHistory();
  const orgId = useSelector(fullstorySelectors.getOrgId);
  const specObjectsBySlug = useSelector(allSpecObjectsBySlug);
  const activeObjectDetails = useMemo(() => {
    if (objectDetails) {
      return objectDetails
        .map(od => toObjectDetailsWithRecords(od, specObjectsBySlug))
        .filter(o => !o.archived);
    }
    return [];
  }, [objectDetails, specObjectsBySlug]);
  return (
    <Box
      data-component="ReferencesTooltipBody"
      display="flex"
      flexDirection="column"
      gap={12}
      width={300}
    >
      <Box>
        <Text appearance="bodyMediumBold">
          <FormattedMessage defaultMessage="References" id="nCQY4D" />
        </Text>
      </Box>
      {loading && <Spinner />}
      {!loading && activeObjectDetails.length === 0 && <EmptyState objectType={objectType} />}
      {!loading && activeObjectDetails.length > 0 && (
        <Box>
          {activeObjectDetails.slice(0, 5).map(obj => {
            const iconName = getObjectIconName(obj.objectType);
            return (
              <Box key={obj.id} paddingBottom={8} display="flex" gap={4}>
                {iconName && (
                  <Box flexShrink={0}>
                    <Icon name={iconName} size={16} />
                  </Box>
                )}
                <Text appearance="bodySmall" noWrap>
                  {obj.name}
                </Text>
              </Box>
            );
          })}
          {activeObjectDetails.length > 5 && (
            <InlineLink
              type="button"
              onClick={e => {
                e.stopPropagation();
                history.push(
                  createPath({
                    pathname: generateRoutePath(getObjectTypePath(objectType), {
                      orgId,
                      id: objectId,
                    }),
                    search: getReferencesTab(supportsProperties),
                  }),
                );
              }}
            >
              <FormattedMessage
                defaultMessage="+ {count} more"
                id="bwT0je"
                values={{ count: activeObjectDetails.length - 5 }}
              />
            </InlineLink>
          )}
        </Box>
      )}
    </Box>
  );
};
